export type MutationLockState =
  | { status: 'idle' }
  | { status: 'pending'; message?: string }
  | { status: 'success'; message?: string }
  | { status: 'error'; message?: string }

type Listener = (state: MutationLockState) => void

const SUCCESS_DISPLAY_MS = 1200
const ERROR_DISPLAY_MS = 4000

class MutationLockStore {
  private state: MutationLockState = { status: 'idle' }
  private listeners = new Set<Listener>()
  private inFlight = 0
  private clearTimer: ReturnType<typeof setTimeout> | null = null

  getState(): MutationLockState {
    return this.state
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  begin(message?: string): void {
    this.inFlight += 1
    if (this.clearTimer) {
      clearTimeout(this.clearTimer)
      this.clearTimer = null
    }
    this.setState({ status: 'pending', message: message ?? 'Saving your change…' })
  }

  succeed(message: string = 'Saved'): void {
    this.inFlight = Math.max(0, this.inFlight - 1)
    if (this.inFlight > 0) {
      // Another mutation is still running — don't flash success yet.
      return
    }
    this.setState({ status: 'success', message })
    this.scheduleClear(SUCCESS_DISPLAY_MS)
  }

  fail(message: string = 'Try again soon.'): void {
    this.inFlight = Math.max(0, this.inFlight - 1)
    // Failure overrides any other pending mutations — show the error
    // immediately. The remaining in-flight calls will resolve later but
    // their success messages won't override this error within the
    // dismissal window.
    this.setState({ status: 'error', message })
    this.scheduleClear(ERROR_DISPLAY_MS)
  }

  /**
   * Retire a mutation whose failure the caller's own UI reports (422 and
   * friends), without flashing a generic error over the form's message.
   *
   * It must decrement the in-flight count exactly as succeed() and fail() do.
   * dismiss() alone leaks the count, and a later succeed() then sees work
   * still outstanding and returns early — leaving the overlay stuck on
   * "Saving your change..." for good. That is what a failed pause did: the
   * 422 leaked a count, then the rollback's success could never clear it.
   */
  retire(): void {
    this.inFlight = Math.max(0, this.inFlight - 1)
    if (this.inFlight > 0) {
      // Others are still saving, so the overlay stays up for them.
      return
    }
    this.dismiss()
  }

  dismiss(): void {
    if (this.clearTimer) {
      clearTimeout(this.clearTimer)
      this.clearTimer = null
    }
    this.setState({ status: 'idle' })
  }

  private scheduleClear(ms: number): void {
    if (this.clearTimer) {
      clearTimeout(this.clearTimer)
    }
    this.clearTimer = setTimeout(() => {
      this.clearTimer = null
      // Only clear if we haven't been re-entered (e.g. a new mutation
      // bumped us back to pending).
      if (this.state.status === 'success' || this.state.status === 'error') {
        this.setState({ status: 'idle' })
      }
    }, ms)
  }

  private setState(next: MutationLockState): void {
    this.state = next
    this.listeners.forEach((listener) => listener(next))
  }
}

export const mutationLockStore = new MutationLockStore()
